import { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../store';
import { useFocusTrap } from '../motion';
import { EULA_VERSION, acceptanceRecord, eulaClauses, eulaIntro, readToEnd } from '../eula';

/**
 * The terms, before anything else.
 *
 * NOT A DIALOG THAT CAN BE DISMISSED, and deliberately not built on the same
 * machinery as the setup questions. There is no Escape, nothing behind it, and
 * no way past it but the button — because the one thing this must never do is
 * let somebody see the board and agree afterwards.
 *
 * Two things are required rather than one: the box has to be ticked AND the
 * button pressed. Continuing does not imply consent, and a single button could
 * be pressed to make a screen go away.
 */
export default function Eula({ readOnly = false, close }: { readOnly?: boolean; close?: () => void }) {
  const myEmail = (useApp((s) => s.user?.email) ?? '').toLowerCase();
  const setPersonProfile = useApp((s) => s.setPersonProfile);
  const signOutUser = useApp((s) => s.signOutUser);
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef);
  const [ticked, setTicked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [trouble, setTrouble] = useState('');
  /**
   * Whether the last clause has been on the screen.
   *
   * Agreeing to something you have not seen the end of is the weakest kind of
   * agreement there is. The tick is not offered until it has been.
   */
  const [seenEnd, setSeenEnd] = useState(false);
  const body = useRef<HTMLDivElement>(null);

  const clauses = eulaClauses();

  const check = useCallback(() => {
    const el = body.current;
    if (el) setSeenEnd(readToEnd(el.scrollTop, el.clientHeight, el.scrollHeight));
  }, []);

  /*
   * Asked once on arrival AND whenever the window changes size, not only on
   * scroll. On a tall screen the terms may not overflow at all — there is no
   * bottom to reach, so waiting for a scroll that will never happen would trap
   * everybody on a large monitor behind a tick they cannot press.
   */
  useEffect(() => {
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [check, clauses.length]);

  const accept = () => {
    setSaving(true);
    // If this cannot be recorded, it has not been accepted. Letting somebody in
    // on a promise nobody wrote down is the one outcome worth refusing.
    void setPersonProfile(myEmail, acceptanceRecord(Date.now())).then(
      () => setSaving(false),
      (e: unknown) => {
        setSaving(false);
        setTrouble(
          `Your agreement could not be recorded, so you have not been let in — ${
            e instanceof Error ? e.message : String(e)
          }`
        );
      }
    );
  };

  return (
    <div className="overlay eula-overlay" role="dialog" aria-modal="true" aria-label="Terms of Use">
      <div ref={panelRef} tabIndex={-1} className="eula">
        <div className="eula-head">
          <h2>Terms of Use</h2>
          <p className="hint">{eulaIntro()}</p>
        </div>

        <div className="eula-body" ref={body} onScroll={check}>
          {clauses.map((c) => (
            <section key={c.heading}>
              <h3>{c.heading}</h3>
              {c.body.map((p, n) => (
                <p key={n}>{p}</p>
              ))}
            </section>
          ))}
          <p className="eula-version">Version {EULA_VERSION}</p>
        </div>

        {readOnly ? (
          <div className="eula-foot">
            <button className="btn btn-primary" onClick={close}>
              Close
            </button>
          </div>
        ) : (
          <div className="eula-foot">
            {trouble && (
              <p className="eula-trouble" role="alert">
                {trouble}
              </p>
            )}
            {/* Said where the tick is, so somebody reaching for it and finding
                it dead is told why in the same glance. */}
            {!seenEnd && (
              <p className="eula-unread">Read to the end of the terms before you can agree.</p>
            )}
            <label className={`eula-tick ${seenEnd ? '' : 'is-locked'}`}>
              <input
                type="checkbox"
                checked={ticked}
                disabled={!seenEnd}
                onChange={(e) => setTicked(e.target.checked)}
              />
              <span>I have read these terms and I agree to them.</span>
            </label>
            <div className="eula-actions">
              {/* Both conditions, again. The tick already requires the reading,
                  so this is belt and braces on the act that actually binds. */}
              <button className="btn btn-primary" disabled={!ticked || !seenEnd || saving} onClick={accept}>
                {saving ? 'Recording…' : 'Agree and continue'}
              </button>
              {/* Refusing has to be possible, and has to do something. Without
                  this the only way out of a screen you do not agree with is to
                  close the tab, which records nothing and teaches nobody. */}
              <button className="btn" onClick={signOutUser}>
                I do not agree — sign me out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
